import crews from "csi/access/crews";
import { ControllerInterface } from "csi/action/Controller";
import { resolveNode } from "csi/service/nodeService";
import { C8Node, cnl } from "csi/typings";
import { ILocaleProvider } from "designSystem/locale";
import ProductHierarchySelectStore from "./ProductHierarchySelectStore";

/**
 * Filter store by Food Create Roles in leaf folders
 * @param node the node to check if filtering is necessary
 */
function filterProductHierarchyFolder(node: C8Node, hierarchy: C8Node, leafLevel: number): boolean {
	const FOOD_CREATE_ROLES = ["Food Administrator"];

	return (
		crews.isInGlobalRoles(FOOD_CREATE_ROLES) || 
        crews.isInRoles(FOOD_CREATE_ROLES, hierarchy) ||
        node.Level !== leafLevel ||
        crews.isInRoles(FOOD_CREATE_ROLES, node)
	);
}

type HierarchyInfo = {
    id: string;
    name: string;
}

/**
 * edits hierarchyStructure and hierarchyMap by traversing the nodes of hierarchy folders
 * @param urls list of project hierarchy folder urls
 * @param hierarchyStructure map of folder names to the list of their children names
 * @param hierarchy node of the highest level hierarchy
 * @param leafLevel level of the leaf node
 * @returns list of names of your children
 */
async function traverse(urls: string[], hierarchyStructure: object, hierarchy: C8Node, leafLevel: number): Promise<object[]> {
	const children: C8Node[] = await Promise.all(urls.map((url) => resolveNode(url)));

	return await Promise.all(children.filter((child) => filterProductHierarchyFolder(child, hierarchy, leafLevel)).map(async (child: C8Node): Promise<HierarchyInfo> => {
		hierarchyStructure[child.$URL] = await traverse(child.Hierarchy, hierarchyStructure, hierarchy, leafLevel);
		hierarchyStructure[child.$URL].splice(0, 0, { id: "", name: "" });
		return { id: child.$URL, name: child.$Name };
	}));
}

/**
 * pushes in-order the names of project hierarchy foldre subtypes
 * @param urls list of project hierarchy folder subtype urls
 * @param hierarchyTypeNames the list of names of hierarchy folder subtypes
 * @returns void
 */
async function getHierarchyNames(urls: string[], hierarchyTypeNames: string[]): Promise<void> {
	if (!urls) {
		return;
	}

	const children: C8Node[] = await Promise.all(urls.map((url) => resolveNode(url)));

	children.forEach((child) => {
		hierarchyTypeNames.push(child.$Name);
	});
}

/** create product hierarchy select store */
export async function createProductHierarchySelectStore(actionController: ControllerInterface, locale: ILocaleProvider, onChange?: Function): Promise<ProductHierarchySelectStore> {
	const cache = actionController.getContext().cache;
	const node: C8Node = actionController.getContext().getNode();

	/** urls of all product hierarchy folder types */
	const folderTypeUrls: string[] =
        cache.getPathNode(
        	node,
        	"Child:ProductHierarchyDetails/Child:ProductHierarchy/Child:Subtype/Child:Settings",
        ).ProductHierarchyFolders;

	/** url of highest level product hierarchy */
	const hierarchy: C8Node = cache.getPathNode(node, "Child:ProductHierarchyDetails/Child:ProductHierarchy");

	const hierarchyStructure: Object = { "": [{ id: "", name: "" }] };
	const hierarchyTypeNames: string[] = [locale.resource("ProductHierarchy.Name")];

	await Promise.all([
		traverse([hierarchy.$URL], hierarchyStructure, hierarchy, folderTypeUrls.length),
		getHierarchyNames(folderTypeUrls, hierarchyTypeNames)]);

	/** returns action controller, localization, onChange function and hierarchyInformation */
	return new ProductHierarchySelectStore(actionController, locale, { hierarchyStructure, hierarchyTypeNames }, onChange);
}
